import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import categorias from "@/data/categorias.json";
import { getSalmos, pesquisarSalmos, getTitulo } from "@/lib/salmos";
import { useLang } from "@/lib/i18n";
import SalmoDoDia from "@/components/SalmoDoDia";
import CategoriaCard from "@/components/CategoriaCard";
import SalmoCard from "@/components/SalmoCard";
import NewsletterCTA from "@/components/NewsletterCTA";
import OrnamentoDourado from "@/components/OrnamentoDourado";
import SEO from "@/components/SEO";

export default function HomePage() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const todos = useMemo(() => getSalmos(), []);
  const destacados = useMemo(() => todos.filter((s) => s.destacado), [todos]);
  const resultados = useMemo(() => pesquisarSalmos(q, lang), [q, lang]);

  return (
    <div className="relative" data-testid="home-page">
      <SEO
        title="salmos.pt — Palavras que Atravessam o Tempo"
        description="Lê, medita e partilha os 150 Salmos em português, inglês, espanhol e francês."
      />

      {/* HERO */}
      <section className="relative px-6 pt-24 pb-32 overflow-hidden">
        <div className="particles-bg pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-5xl text-center">
          <div className="font-display text-[10px] uppercase tracking-[0.42em] text-[var(--cor-dourado)] animate-fade-in">
            {t.tagline}
          </div>
          <h1
            className="font-display text-7xl sm:text-8xl lg:text-[10rem] text-[var(--cor-texto)] font-light mt-8 leading-[0.9] tracking-tight animate-fade-up"
            data-testid="hero-title"
          >
            {t.salmos}
          </h1>
          <OrnamentoDourado className="mt-10 animate-fade-in-delay" />
          <p className="font-serif italic text-xl md:text-2xl text-[var(--cor-texto-2)] mt-10 max-w-2xl mx-auto leading-relaxed animate-fade-up-delay">
            {t.hero_subtitle}
          </p>

          <div className="mt-16 max-w-2xl mx-auto relative animate-fade-up-delay-2">
            <div className="flex items-center gap-3 border-b-2 border-[var(--cor-borda)] focus-within:border-[var(--cor-dourado)] px-3 py-4 transition-colors">
              <Search className="h-5 w-5 text-[var(--cor-dourado)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.search_placeholder}
                className="flex-1 bg-transparent text-lg font-serif text-[var(--cor-texto)] placeholder:text-[var(--cor-texto-2)] outline-none"
                data-testid="hero-search-input"
              />
            </div>
            {q && (
              <div className="mt-4 rounded-sm border border-[var(--cor-borda)] bg-[var(--cor-fundo-2)] overflow-hidden text-left">
                {resultados.length === 0 ? (
                  <div className="px-6 py-5 font-serif italic text-[var(--cor-texto-2)]">{t.nothing_found}</div>
                ) : (
                  resultados.slice(0, 6).map((s, i) => (
                    <button
                      key={s.numero}
                      onClick={() => navigate(`/salmo/${s.numero}`)}
                      className="block w-full px-6 py-4 hover:bg-[var(--cor-fundo-3)] transition-colors border-b border-[var(--cor-borda)] last:border-0 animate-fade-in"
                      style={{ animationDelay: `${i * 60}ms` }}
                      data-testid={`hero-search-result-${s.numero}`}
                    >
                      <div className="font-display text-[var(--cor-dourado)] text-xs uppercase tracking-widest">{t.salmo} {s.numero}</div>
                      <div className="font-serif text-[var(--cor-texto)] text-lg">{getTitulo(s, lang)}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="px-6 pb-32">
        <SalmoDoDia />
      </section>

      <section className="px-6 mx-auto max-w-6xl" data-testid="categorias-section">
        <div className="text-center mb-16">
          <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.por_situacao}</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--cor-texto)] mt-5 font-light">{t.encontra_salmo}</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {categorias.map((c) => (
            <CategoriaCard key={c.slug} categoria={c} />
          ))}
        </div>
      </section>

      <section className="px-6 mx-auto max-w-6xl mt-32" data-testid="salmos-section">
        <div className="text-center mb-16">
          <div className="font-display text-[10px] uppercase tracking-[0.32em] text-[var(--cor-dourado)]">{t.salmo_destaque}</div>
          <h2 className="font-display text-4xl md:text-5xl text-[var(--cor-texto)] mt-5 font-light">{t.salmos_essenciais}</h2>
          <p className="font-serif italic text-[var(--cor-texto-2)] mt-5 max-w-xl mx-auto">{t.descricao_destaques}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {destacados.map((s) => (
            <SalmoCard key={s.numero} salmo={s} />
          ))}
        </div>
      </section>

      <NewsletterCTA />
    </div>
  );
}
